'use client';

import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { Modal } from '@shared/ui';
import type { BaseModalProps } from '@shared/types/modal.types';
import { reverseGeocode } from '@/src/shared/lib/geocoding';

// Dynamic import for Leaflet to avoid SSR issues
const LocationMap = dynamic(() => import('./components/LocationMapLeaflet').then((mod) => ({ default: mod.LocationMap })), {
  ssr: false,
  loading: () => <div className="flex items-center justify-center h-[400px]">Loading map...</div>,
});

interface LocationData {
  address: string;
  city: string;
  province: string;
  postalCode: string;
  latitude: number;
  longitude: number;
}

interface LocationPickerModalProps extends BaseModalProps {
  onLocationSelected?: (location: LocationData) => Promise<void> | void;
  initialLocation?: Partial<LocationData>;
  title?: string;
}

/**
 * Location Picker Modal - Map-first approach
 *
 * Simple flow:
 * 1. Search for address OR click on map OR use GPS
 * 2. Reverse geocode to get address details
 * 3. Confirm selection
 */
export function LocationPickerModal({
  isOpen,
  onClose,
  onLocationSelected,
  initialLocation,
  title = 'Set Your Location',
}: LocationPickerModalProps) {
  const [locationData, setLocationData] = useState<Partial<LocationData>>({
    address: initialLocation?.address || '',
    city: initialLocation?.city || '',
    province: initialLocation?.province || '',
    postalCode: initialLocation?.postalCode || '',
    latitude: initialLocation?.latitude,
    longitude: initialLocation?.longitude,
  });

  const [isGeocoding, setIsGeocoding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Canadian provinces
  const PROVINCES = [
    { code: 'AB', name: 'Alberta' },
    { code: 'BC', name: 'British Columbia' },
    { code: 'MB', name: 'Manitoba' },
    { code: 'NB', name: 'New Brunswick' },
    { code: 'NL', name: 'Newfoundland and Labrador' },
    { code: 'NS', name: 'Nova Scotia' },
    { code: 'NT', name: 'Northwest Territories' },
    { code: 'NU', name: 'Nunavut' },
    { code: 'ON', name: 'Ontario' },
    { code: 'PE', name: 'Prince Edward Island' },
    { code: 'QC', name: 'Quebec' },
    { code: 'SK', name: 'Saskatchewan' },
    { code: 'YT', name: 'Yukon' },
  ];

  useEffect(() => {
    if (initialLocation) {
      setLocationData(initialLocation);
    }
  }, [initialLocation]);

  // Handle location selected from map (click or search)
  const handleMapLocationSelect = async (coords: { latitude: number; longitude: number }) => {
    setIsGeocoding(true);
    setError(null);

    try {
      const address = await reverseGeocode(coords.latitude, coords.longitude);

      if (address && address.city && address.province) {
        // Full geocoding success
        setLocationData({
          address: address.address,
          city: address.city,
          province: address.province,
          postalCode: address.postalCode,
          latitude: coords.latitude,
          longitude: coords.longitude,
        });
      } else {
        // Partial or no geocoding - set coordinates with whatever data we have
        setLocationData((prev) => ({
          ...prev,
          address: address?.address || '',
          city: address?.city || '',
          province: address?.province || '',
          postalCode: address?.postalCode || '',
          latitude: coords.latitude,
          longitude: coords.longitude,
        }));
      }
    } catch (err) {
      console.error('[LocationPicker] Reverse geocoding failed:', err);
      // Set coordinates even if geocoding fails
      setLocationData((prev) => ({
        ...prev,
        latitude: coords.latitude,
        longitude: coords.longitude,
      }));
    } finally {
      setIsGeocoding(false);
    }
  };

  // Handle GPS button
  const handleUseGPS = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setIsGeocoding(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        await handleMapLocationSelect({ latitude, longitude });
      },
      (err) => {
        // Enhanced error logging for geolocation errors
        console.error('[LocationPicker] GPS error details:', {
          code: err?.code,
          message: err?.message,
          PERMISSION_DENIED: err?.PERMISSION_DENIED,
          POSITION_UNAVAILABLE: err?.POSITION_UNAVAILABLE,
          TIMEOUT: err?.TIMEOUT,
          fullError: err,
        });

        setIsGeocoding(false);

        // Safely handle error codes with fallback
        const errorCode = err?.code;

        if (errorCode === 1 || errorCode === err?.PERMISSION_DENIED) {
          setError('Location access denied. Please enable location in browser settings.');
        } else if (errorCode === 2 || errorCode === err?.POSITION_UNAVAILABLE) {
          setError('Location unavailable. Please try again or select on map.');
        } else if (errorCode === 3 || errorCode === err?.TIMEOUT) {
          setError('Location request timed out. Please try again.');
        } else {
          // Fallback with detailed message
          const errorMsg = err?.message || 'Unknown error';
          setError(`Could not get your location: ${errorMsg}. Please select on map.`);
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Validate and submit
  const handleConfirm = async () => {
    if (locationData.latitude == null || locationData.longitude == null) {
      setError('Please select a location on the map');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const locationToSave: LocationData = {
        address: locationData.address || '',
        city: locationData.city || '',
        province: locationData.province || '',
        postalCode: locationData.postalCode || '',
        latitude: locationData.latitude,
        longitude: locationData.longitude,
      };

      // Call the callback (may be async)
      await onLocationSelected?.(locationToSave);

      // Close modal and reset form on success
      onClose();
      setLocationData({
        address: '',
        city: '',
        province: '',
        postalCode: '',
        latitude: undefined,
        longitude: undefined,
      });
    } catch (err) {
      console.error('[LocationPicker] Failed to save location:', err);
      setError('Failed to save location. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Check if location is valid for confirmation (only coordinates required)
  const isLocationValid = locationData.latitude != null && locationData.longitude != null;

  // Check if location has full address details
  const hasFullAddress = !!(locationData.city && locationData.province);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      animation="slide-up"
      title={title}
      description="Click on the map to set your base location. Address details are optional."
    >
      <div className="space-y-4">
        {/* Privacy Notice */}
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex gap-2">
            <span className="text-blue-600 flex-shrink-0">ℹ️</span>
            <div>
              <p className="text-sm font-medium text-blue-800">Privacy Notice</p>
              <p className="text-xs text-blue-600 mt-1">
                Your exact address is private. Clients only see your city and service area radius.
              </p>
            </div>
          </div>
        </div>

        {/* GPS Button */}
        <button
          type="button"
          onClick={handleUseGPS}
          disabled={isGeocoding}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="text-xl">📍</span>
          <span className="text-sm font-medium text-gray-700">
            {isGeocoding ? 'Getting location...' : 'Use My Current Location (GPS)'}
          </span>
        </button>

        {/* Map with search */}
        <LocationMap
          latitude={locationData.latitude}
          longitude={locationData.longitude}
          onLocationSelect={handleMapLocationSelect}
          showSearch={true}
          height="350px"
        />

        {/* Selected Location Display */}
        {locationData.latitude != null && locationData.longitude != null && (
          <div className={`p-4 rounded-lg border ${hasFullAddress ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
            <div className="flex items-start gap-3">
              <span className="text-xl">{hasFullAddress ? '✅' : '📍'}</span>
              <div className="flex-1">
                <p className={`font-medium ${hasFullAddress ? 'text-green-800' : 'text-blue-800'}`}>
                  {hasFullAddress ? 'Location Set with Address' : 'Location Set (Coordinates Only)'}
                </p>
                {hasFullAddress && (
                  <p className={`text-sm mt-1 ${hasFullAddress ? 'text-green-700' : 'text-blue-700'}`}>
                    {[locationData.address, locationData.city, locationData.province, locationData.postalCode]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                )}
                {!hasFullAddress && (
                  <p className="text-sm mt-1 text-blue-700">
                    You can add address details in the form below (optional).
                  </p>
                )}
                <p className={`text-xs mt-1 ${hasFullAddress ? 'text-green-600' : 'text-blue-600'}`}>
                  Coordinates: {locationData.latitude.toFixed(4)}, {locationData.longitude.toFixed(4)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Address Entry Form - always available after selecting coordinates */}
        {locationData.latitude != null && locationData.longitude != null && (
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
            <p className="text-sm font-medium text-gray-700">
              📝 Address details (optional):
            </p>

            <div className="grid grid-cols-2 gap-4">
              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  City <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={locationData.city || ''}
                  onChange={(e) => setLocationData((prev) => ({ ...prev, city: e.target.value }))}
                  placeholder="e.g., Calgary"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Province */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Province <span className="text-gray-400">(optional)</span>
                </label>
                <select
                  value={locationData.province || ''}
                  onChange={(e) => setLocationData((prev) => ({ ...prev, province: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select...</option>
                  {PROVINCES.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Optional: Postal Code */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Postal Code <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={locationData.postalCode || ''}
                onChange={(e) => setLocationData((prev) => ({ ...prev, postalCode: e.target.value.toUpperCase() }))}
                placeholder="e.g., T2P 1J9"
                maxLength={7}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Optional: Address */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Address <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={locationData.address || ''}
                onChange={(e) => setLocationData((prev) => ({ ...prev, address: e.target.value }))}
                placeholder="e.g., 123 Main Street"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 flex items-center gap-2">
              <span>⚠️</span>
              {error}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isGeocoding || isSaving || !isLocationValid}
            className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {isGeocoding ? 'Verifying...' : isSaving ? 'Saving...' : 'Confirm & Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
